import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, ChevronDown, ChevronUp, Rocket, Mail } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STAGES = [
  { key: "i18n", label: "Garde-fou i18n" },
  { key: "api", label: "API paiements" },
  { key: "e2e", label: "E2E web + iOS + Android" },
];

/** Historique des vérifications pré-déploiement (preflight.sh). */
export const PreflightPanel = () => {
  const [reports, setReports] = useState(null);
  const [open, setOpen] = useState(null); // report_id déplié

  useEffect(() => {
    apiClient.get("/admin/preflight-reports")
      .then(({ data }) => setReports(data))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  if (!reports) return <div className="text-mute text-sm py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;

  return (
    <div className="space-y-3" data-testid="admin-preflight-panel">
      <div className="bg-elevated rounded-2xl p-4 text-sm text-mute flex items-start gap-3">
        <Rocket className="w-5 h-5 text-brand-red shrink-0 mt-0.5" />
        <span>
          Chaque exécution de <code className="bg-white px-1.5 py-0.5 rounded text-xs">bash /app/scripts/preflight.sh</code> (à lancer
          avant chaque déploiement) est archivée ici et envoyée par email à contact@neldaari.com.
          Vert = déploiement autorisé · Rouge = ne pas déployer.
        </span>
      </div>

      {reports.length === 0 && (
        <div className="bg-white border border-sand rounded-2xl p-10 text-center text-mute" data-testid="preflight-empty">
          Aucun rapport preflight pour l'instant.
        </div>
      )}

      {reports.map((r) => {
        const ok = r.status === "OK";
        const expanded = open === r.report_id;
        return (
          <div key={r.report_id} data-testid={`preflight-report-${r.report_id}`}
               className={`bg-white border rounded-2xl overflow-hidden ${ok ? "border-sand" : "border-brand-red/50"}`}>
            <button
              onClick={() => setOpen(expanded ? null : r.report_id)}
              data-testid={`preflight-toggle-${r.report_id}`}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-elevated/50"
            >
              {ok
                ? <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0" />
                : <XCircle className="w-5 h-5 text-brand-red shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">
                  {ok ? "Déploiement autorisé" : "NE PAS DÉPLOYER"}
                </div>
                <div className="text-xs text-mute">
                  {new Date(r.ran_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-2">
                {STAGES.map((s) => (
                  <span key={s.key}
                        className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${r.stages?.[s.key] ? "bg-[#E7F6EE] text-brand-green" : "bg-[#FDECEE] text-brand-red"}`}>
                    {s.label}
                  </span>
                ))}
              </div>
              {r.email_id && <Mail className="w-4 h-4 text-mute shrink-0" title="Rapport envoyé par email" />}
              {expanded ? <ChevronUp className="w-4 h-4 text-mute" /> : <ChevronDown className="w-4 h-4 text-mute" />}
            </button>
            {expanded && (
              <div className="border-t border-sand px-4 py-3 bg-[#FBFAF7]" data-testid={`preflight-detail-${r.report_id}`}>
                <div className="sm:hidden flex flex-wrap gap-2 mb-2">
                  {STAGES.map((s) => (
                    <span key={s.key}
                          className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${r.stages?.[s.key] ? "bg-[#E7F6EE] text-brand-green" : "bg-[#FDECEE] text-brand-red"}`}>
                      {s.label}
                    </span>
                  ))}
                </div>
                <div className="font-mono text-[11px] leading-relaxed whitespace-pre-wrap text-ink">
                  {(r.e2e_detail || []).join("\n") || "Détail e2e indisponible"}
                </div>
                {r.errors?.length > 0 && (
                  <div className="mt-2 font-mono text-[11px] text-brand-red whitespace-pre-wrap">
                    {r.errors.join("\n")}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
